import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { atd } from "@/lib/api";
import { toast } from "sonner";
import { HeartHandshake, Ticket, Users2, Sparkles, BookOpen, ArrowLeft, LogOut, Shield, Clock } from "lucide-react";

export default function Atendimento() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [nps, setNps] = useState([]);
  const [kb, setKb] = useState([]);

  useEffect(() => { load(); }, [tab]);
  const load = async () => {
    try {
      if (tab === "dashboard") setDash((await atd.dashboard()).data);
      if (tab === "tickets") setTickets((await atd.tickets()).data);
      if (tab === "clientes") setClientes((await atd.clientes360()).data);
      if (tab === "nps") setNps((await atd.nps()).data);
      if (tab === "kb") setKb((await atd.kb()).data);
    } catch { toast.error("Erro"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0B1A] flex flex-col">
      <header className="bg-gradient-to-r from-[#1A1D42] to-[#252952] border-b border-[#B28F4B]/25 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-5 py-3 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#0A0B1A] border-2 border-[#B28F4B] flex items-center justify-center"><HeartHandshake size={20} className="text-[#B28F4B]"/></div>
          <div className="flex-1">
            <div className="text-[#B28F4B] font-bold font-heading text-lg">SOEASY · Atendimento ao Cliente</div>
            <div className="text-white/60 text-[10px] uppercase tracking-widest">Customer Success · SAC 24/7 · SLA · LGPD · Decreto 11.034/2022</div>
          </div>
          <div className="hidden md:flex gap-2">
            <span className="px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9px] font-bold uppercase tracking-widest"><Shield size={10} className="inline mr-1"/>LGPD</span>
            <span className="px-2.5 py-1 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-300 text-[9px] font-bold uppercase tracking-widest">Dec. 11.034/2022</span>
            <span className="px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/40 text-purple-300 text-[9px] font-bold uppercase tracking-widest">CDC</span>
          </div>
          <button onClick={() => navigate("/setores")} data-testid="btn-back-setores" className="px-3 py-1.5 rounded-md border border-[#B28F4B]/40 text-white text-xs font-bold uppercase tracking-widest">Setores</button>
          <button onClick={() => { logout(); navigate("/login"); }} className="px-3 py-1.5 rounded-md bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold uppercase tracking-widest">Sair</button>
        </div>
        <div className="max-w-[1600px] mx-auto px-5 flex gap-1 overflow-x-auto">
          {[
            { id: "dashboard", label: "Comando", icon: HeartHandshake },
            { id: "tickets", label: "Tickets & SAC", icon: Ticket },
            { id: "clientes", label: "Clientes 360°", icon: Users2 },
            { id: "nps", label: "NPS", icon: Sparkles },
            { id: "kb", label: "Base Conhecimento", icon: BookOpen },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-atd-${t.id}`}
              className={`px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest border-b-2 flex items-center gap-1.5 ${tab===t.id?"border-[#B28F4B] text-[#B28F4B]":"border-transparent text-white/60 hover:text-white"}`}>
              <t.icon size={13}/> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto p-5 fade-up">
        {tab === "dashboard" && dash && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AtdKpi label="Tickets Ativos" value={(dash.novos||0)+(dash.em_atendimento||0)+(dash.aguardando_cliente||0)} sub={`${dash.novos} novos`} color="#22D3EE"/>
              <AtdKpi label="SLA Compliance" value={`${dash.sla_compliance_pct}%`} sub={`${dash.vencidos_sla} vencidos`} color={dash.sla_compliance_pct >= 90 ? "#10B981" : "#F59E0B"}/>
              <AtdKpi label="NPS Score" value={dash.nps_score ?? "—"} sub={`${dash.nps_total_respostas} respostas`} color="#B28F4B"/>
              <AtdKpi label="Críticos" value={dash.criticos} sub="atenção imediata" color="#EF4444"/>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
                <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Distribuição por Canal</div>
                {Object.entries(dash.por_canal || {}).map(([k, v]) => {
                  const max = Math.max(...Object.values(dash.por_canal), 1);
                  return (<div key={k} className="mb-2"><div className="flex justify-between text-xs mb-1"><span className="text-white uppercase">{k}</span><span className="text-[#B28F4B] font-mono font-bold">{v}</span></div><div className="h-1.5 bg-[#0A0B1A] rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{width: `${v/max*100}%`}}/></div></div>);
                })}
              </div>
              <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
                <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Distribuição por Categoria</div>
                {Object.entries(dash.por_categoria || {}).map(([k, v]) => {
                  const max = Math.max(...Object.values(dash.por_categoria), 1);
                  return (<div key={k} className="mb-2"><div className="flex justify-between text-xs mb-1"><span className="text-white uppercase">{k.replace(/_/g," ")}</span><span className="text-[#B28F4B] font-mono font-bold">{v}</span></div><div className="h-1.5 bg-[#0A0B1A] rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#B28F4B] to-[#D4AF37]" style={{width: `${v/max*100}%`}}/></div></div>);
                })}
              </div>
            </div>
          </div>
        )}

        {tab === "tickets" && (
          <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0B1A]/60 border-b border-[#B28F4B]/25 text-left text-[9px] font-bold uppercase tracking-[0.2em] text-[#B28F4B]"><tr><th className="p-3">Protocolo</th><th className="p-3">Cliente</th><th className="p-3">Assunto</th><th className="p-3">Canal</th><th className="p-3">Categoria</th><th className="p-3">Prior.</th><th className="p-3">SLA</th><th className="p-3">Status</th></tr></thead>
              <tbody>
                {tickets.map(t => (
                  <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-3 text-[#B28F4B] font-mono text-[10px]">{t.protocolo}</td>
                    <td className="p-3 text-white text-xs font-bold">{t.cliente_nome}</td>
                    <td className="p-3 text-[#A1A4C7] text-xs max-w-xs truncate">{t.assunto}</td>
                    <td className="p-3 text-[#A1A4C7] text-[10px] uppercase">{t.canal}</td>
                    <td className="p-3 text-[#A1A4C7] text-[10px] uppercase">{t.categoria?.replace(/_/g," ")}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${t.prioridade==="critica"?"bg-red-500/25 text-red-300":t.prioridade==="alta"?"bg-orange-500/20 text-orange-400":t.prioridade==="media"?"bg-amber-500/15 text-amber-400":"bg-emerald-500/15 text-emerald-400"}`}>{t.prioridade}</span></td>
                    <td className="p-3"><span className={`text-[10px] font-mono font-bold ${t.sla_status==="vencido"?"text-red-400":t.sla_status==="critico"?"text-orange-400":t.sla_status==="cumprido"?"text-emerald-400":"text-white/60"}`}>{t.sla_status || "—"}</span></td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded bg-[#B28F4B]/15 text-[#B28F4B] text-[9px] font-bold uppercase">{t.status?.replace(/_/g," ")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "clientes" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {clientes.map(c => (
              <div key={c.id} className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`w-3 h-3 rounded-full ${c.health==="verde"?"bg-emerald-500":c.health==="amarelo"?"bg-amber-500":"bg-red-500"}`}/>
                  <span className="text-[9px] uppercase tracking-widest text-[#B28F4B] font-mono">{c.codigo}</span>
                </div>
                <div className="text-white font-bold text-sm mb-1 line-clamp-1">{c.razao_social}</div>
                <div className="text-[#A1A4C7] text-xs mb-3">{c.municipio}/{c.uf}</div>
                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#B28F4B]/20">
                  <div className="text-center"><div className="text-cyan-400 font-mono font-bold text-lg">{c.tickets_abertos}</div><div className="text-[9px] uppercase text-white/50">Abertos</div></div>
                  <div className="text-center"><div className="text-[#B28F4B] font-mono font-bold text-lg">{c.tickets_total}</div><div className="text-[9px] uppercase text-white/50">Total</div></div>
                  <div className="text-center"><div className="text-emerald-400 font-mono font-bold text-lg">{c.nps_medio ?? "—"}</div><div className="text-[9px] uppercase text-white/50">NPS</div></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "nps" && (
          <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0B1A]/60 border-b border-[#B28F4B]/25 text-left text-[9px] font-bold uppercase tracking-[0.2em] text-[#B28F4B]"><tr><th className="p-3">Cliente</th><th className="p-3 text-center">Nota</th><th className="p-3">Categoria</th><th className="p-3">Comentário</th><th className="p-3">Respondido em</th></tr></thead>
              <tbody>
                {nps.map(n => (
                  <tr key={n.id} className="border-b border-white/5">
                    <td className="p-3 text-white font-bold text-xs">{n.cliente_nome}</td>
                    <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-sm font-mono font-bold ${n.nota>=9?"bg-emerald-500/20 text-emerald-400":n.nota>=7?"bg-amber-500/20 text-amber-400":"bg-red-500/20 text-red-400"}`}>{n.nota ?? "—"}</span></td>
                    <td className="p-3 text-[10px] uppercase font-bold tracking-widest" style={{color: n.categoria==="promotor"?"#10B981":n.categoria==="neutro"?"#F59E0B":"#EF4444"}}>{n.categoria}</td>
                    <td className="p-3 text-[#A1A4C7] text-xs italic max-w-md">{n.comentario}</td>
                    <td className="p-3 text-[10px] text-[#6B6E8F] font-mono">{n.respondido_em?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "kb" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {kb.map(a => (
              <div key={a.id} className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded bg-[#B28F4B]/15 text-[#B28F4B] text-[9px] font-bold uppercase tracking-widest">{a.categoria}</span>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${a.visibilidade==="publico"?"bg-emerald-500/15 text-emerald-400":"bg-slate-500/15 text-slate-400"}`}>{a.visibilidade}</span>
                </div>
                <div className="text-white font-bold text-sm mb-2">{a.titulo}</div>
                <div className="text-[#A1A4C7] text-xs line-clamp-3">{a.conteudo}</div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function AtdKpi({ label, value, sub, color }) {
  return (
    <div className="bg-gradient-to-br from-[#131532] to-transparent border border-[#B28F4B]/25 rounded-2xl p-4">
      <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#A1A4C7]">{label}</div>
      <div className="font-mono font-bold text-2xl mt-1" style={{color}}>{value}</div>
      <div className="text-[9px] text-[#6B6E8F] mt-0.5 uppercase tracking-widest">{sub}</div>
    </div>
  );
}
